import { observer } from "mobx-react-lite";
import React, { useMemo, useEffect, useState } from "react";
import { SideOverlay } from "./side-overlay";
import { useStore } from "../helpers/helpers";
import { useTranslation } from "react-i18next";
import { buildLanguageMap } from "../features/survey-builder/utils/survey-utils";

// Original language list (used when no survey is active)
const defaultLanguages = [
  { key: "en", val: "English" },
  { key: "kn", val: "ಕನ್ನಡ" },
  { key: "hi", val: "हिंदी" },
  { key: "tl", val: "తెలుగు" },
  { key: "mr", val: "मराठी" },
  { key: "ta", val: "Tamil" },
  { key: "ml", val: "Malayalam" },
  { key: "gj", val: "Gujarathi" },
  { key: "as", val: "Assamese" },
];

export const LanguageChange = observer(() => {
  const rootStore = useStore();
  const { i18n } = useTranslation();
  const { activeSurveyStore } = rootStore;
  const [selectedLanguage, setSelectedLanguage] = useState(activeSurveyStore.language || "en");

  // Get available languages from survey if active, otherwise use default languages
  const languagesToShow = useMemo(() => {
    if (activeSurveyStore.activeSurvey) {
      // Get languages from survey meta (same logic as mobile and survey-builder.tsx)
      const survey = activeSurveyStore.activeSurvey;
      const availableLanguages = survey.meta?.languages || ["en"];

      // Build language map using the same utility as mobile
      const languageMap = buildLanguageMap(availableLanguages);

      // Return languages in the format expected by the UI
      return Object.keys(languageMap).map((langKey) => ({
        key: langKey,
        surveyKey: langKey, // Same as key for survey languages
        val: languageMap[langKey],
      }));
    }

    // If no survey is active, return default languages
    return defaultLanguages.map((lang) => ({
      key: lang.key,
      surveyKey: lang.key,
      val: lang.val,
    }));
  }, [activeSurveyStore.activeSurvey]);

  // Sync selectedLanguage with activeSurveyStore.language
  useEffect(() => {
    setSelectedLanguage(activeSurveyStore.language || "en");
  }, [activeSurveyStore.language]);

  // Validate current language when survey changes (matching mobile behavior)
  useEffect(() => {
    if (activeSurveyStore.activeSurvey) {
      const survey = activeSurveyStore.activeSurvey;
      const availableLanguages = survey.meta?.languages || ["en"];
      const languageMap = buildLanguageMap(availableLanguages);
      const languageMapKeys = Object.keys(languageMap);
      const defaultLanguage = languageMapKeys && languageMapKeys.length > 0 ? languageMapKeys[0] : "en";

      if (!languageMapKeys.includes(activeSurveyStore.language)) {
        activeSurveyStore.setLanguage(defaultLanguage);
        setSelectedLanguage(defaultLanguage);
      }
    }
  }, [activeSurveyStore.activeSurvey?.id, activeSurveyStore]);

  const handleLanguageSelect = (langKey: string) => {
    setSelectedLanguage(langKey);
  };

  const handleApply = () => {
    if (activeSurveyStore.activeSurvey) {
      activeSurveyStore.setLanguage(selectedLanguage);
    }
    // Also update i18n language for app UI
    i18n.changeLanguage(selectedLanguage);
    rootStore.update_language_Change(false);
  };

  return (
    <SideOverlay
      show={rootStore.languageChange}
      onClose={() => rootStore.update_language_Change(false)}
      title="Choose Language"
      children={
        <div className="flex flex-col">
          <div className="grid grid-cols-3 gap-6 p-4">
            {languagesToShow.map((lang) => {
              const isSelected = selectedLanguage === lang.key;
              return (
                <button
                  key={lang.key}
                  onClick={() => handleLanguageSelect(lang.key)}
                  className={`w-full py-4 px-6 text-lg font-semibold rounded-xl transition-all duration-300 shadow-md flex items-center justify-between ${isSelected
                    ? "bg-[#2167D7] text-white"
                    : "bg-white text-black border border-gray-300 hover:bg-[#E5F0FF]"
                    }`}
                >
                  <span>{lang.val}</span>
                  {isSelected && (
                    <svg
                      className="w-6 h-6"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
            <button
              onClick={() => rootStore.update_language_Change(false)}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-6 py-2 text-white bg-[#2167D7] rounded-lg hover:bg-[#1a56c4] transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      }
    />
  );
});
